import { Guid } from "@iconics/core";
import '@serendie/ui/styles.css';
import './Styles.scss';
import './Dialogs/ThemeDialog/Dark.css';
import './Dialogs/ThemeDialog/HighContrast.css';
import { FadxPlatformApiClientV2, IFadxPlatformApiClientV2 } from "@iconics/providers.fadx-platform-api-client-v2";
import { EmServiceWebEntryApiClientV1, IEmServiceWebEntryApiClientV1 } from "@iconics/providers.data-entry-api-client-v1";
import { DeleteButton } from "./Buttons/DeleteButton/DeleteButton";
import { LanguageDialog } from "./Dialogs/LanguageDialog/LanguageDialog";
import { FileDropZone } from "./Inputs/FileDropZone/FileDropZone";
import { MediaSelector } from "./Inputs/MediaSelector/MediaSelector";
import { MultilingualTextField } from "./Inputs/MultilingualTextField/MultilingualTextField";
import { UserPicker } from "./Inputs/UserPicker/UserPicker";
import { PasswordField } from "./Inputs/PasswordField/PasswordField";
import { Select } from "./Inputs/Select/Select";
import { TextArea } from "./Inputs/TextArea/TextArea";
import { TextField } from "./Inputs/TextField/TextField";
import { ApiErrorDialog } from "./Dialogs/ApiErrorDialog/ApiErrorDialog";
import { ConfirmDialog } from "./Dialogs/ConfirmDialog/ConfirmDialog";
import { AlertDialog } from "./Dialogs/AlertDialog/AlertDialog";
import { TreeDataGrid } from "./Lists/TreeDataGrid/TreeDataGrid";
import { TickSymbol } from "./Lists/DataGrid/TickSymbol";

export class Components
{
    public fadxPlatformApiClientV2!: IFadxPlatformApiClientV2;
    public dataEntryApiClientV1!: IEmServiceWebEntryApiClientV1;
    public isDevMode = false;

    public initialise(settings: {
        platformApiV2Url: string,
        dataEntryApiV1Url: string,
        isDevMode?: boolean,
    }): Components
    {
        this.fadxPlatformApiClientV2 = new FadxPlatformApiClientV2({ baseUrl: settings.platformApiV2Url });
        this.dataEntryApiClientV1 = new EmServiceWebEntryApiClientV1({ baseUrl: settings.dataEntryApiV1Url });
        this.isDevMode = settings.isDevMode ?? false;
        window.addEventListener('resize', this.resizeListener);
        return this;
    }

    public configureDeleteButton(settings: {
        buttonLabel?: string;
        dialogTitle?: string;
        dialogMessage?: string;
        dialogConfirmDeleteLabel?: string;
    }): Components
    {
        DeleteButton.buttonLabel = settings.buttonLabel ?? DeleteButton.buttonLabel;
        DeleteButton.dialogTitle = settings.dialogTitle ?? DeleteButton.dialogTitle;
        DeleteButton.dialogMessage = settings.dialogMessage ?? DeleteButton.dialogMessage;
        DeleteButton.dialogConfirmDeleteLabel = settings.dialogConfirmDeleteLabel ?? DeleteButton.dialogConfirmDeleteLabel;
        return this;
    }

    public configureAlertDialog(settings: {
        okButtonLabel?: string;
    }): Components
    {
        AlertDialog.okButtonLabel = settings.okButtonLabel ?? AlertDialog.okButtonLabel;
        return this;
    }

    public configureApiErrorDialog(settings: {
        dialogTitle?: string;
        okButtonLabel?: string;
    }): Components
    {
        ApiErrorDialog.dialogTitle = settings.dialogTitle ?? ApiErrorDialog.dialogTitle;
        ApiErrorDialog.okButtonLabel = settings.okButtonLabel ?? ApiErrorDialog.okButtonLabel;
        return this;
    }

    public configureConfirmDialog(settings: {
        okButtonLabel?: string;
        cancelButtonLabel?: string;
    }): Components
    {
        ConfirmDialog.okButtonLabel = settings.okButtonLabel ?? ConfirmDialog.okButtonLabel;
        ConfirmDialog.cancelButtonLabel = settings.cancelButtonLabel ?? ConfirmDialog.cancelButtonLabel;
        return this;
    }

    public configureLanguageDialog(settings: {
        title?: string;
        subTitle?: string;
        languageInputLabel?: string;
        okButtonLabel?: string;
        cancelButtonLabel?: string;
    }): Components
    {
        LanguageDialog.title = settings.title ?? LanguageDialog.title;
        LanguageDialog.subTitle = settings.subTitle ?? LanguageDialog.subTitle;
        LanguageDialog.languageInputLabel = settings.languageInputLabel ?? LanguageDialog.languageInputLabel;
        LanguageDialog.okButtonLabel = settings.okButtonLabel ?? LanguageDialog.okButtonLabel;
        LanguageDialog.cancelButtonLabel = settings.cancelButtonLabel ?? LanguageDialog.cancelButtonLabel;
        return this;
    }

    public configureFileDropZone(settings: {
        prompt?: string;
        selectButtonLabel?: string;
        resetButtonLabel?: string;
    }): Components
    {
        FileDropZone.prompt = settings.prompt ?? FileDropZone.prompt;
        FileDropZone.selectButtonLabel = settings.selectButtonLabel ?? FileDropZone.selectButtonLabel;
        FileDropZone.resetButtonLabel = settings.resetButtonLabel ?? FileDropZone.resetButtonLabel;
        return this;
    }

    public configureMediaSelector(settings: {
        selectButtonLabel?: string;
        dialogTitle?: string;
        duplicateImageError?: string;
        selectedImageLabel?: string;
        okButtonLabel?: string;
        cancelButtonLabel?: string;
    }): Components
    {
        MediaSelector.selectButtonLabel = settings.selectButtonLabel ?? MediaSelector.selectButtonLabel;
        MediaSelector.dialogTitle = settings.dialogTitle ?? MediaSelector.dialogTitle;
        MediaSelector.duplicateImageError = settings.duplicateImageError ?? MediaSelector.duplicateImageError;
        MediaSelector.selectedImageLabel = settings.selectedImageLabel ?? MediaSelector.selectedImageLabel;
        MediaSelector.okButtonLabel = settings.okButtonLabel ?? MediaSelector.okButtonLabel;
        MediaSelector.cancelButtonLabel = settings.cancelButtonLabel ?? MediaSelector.cancelButtonLabel;
        return this;
    }

    public configureMultilingualTextField(settings: {
        requiredLabel?: string;
        unknownLanguageLabel?: string;
        dialogTitle?: string;
        dialogSubTitle?: string;
        okButtonLabel?: string;
        cancelButtonLabel?: string;
    }): Components
    {
        MultilingualTextField.requiredLabel = settings.requiredLabel ?? MultilingualTextField.requiredLabel;
        MultilingualTextField.unknownLanguageLabel = settings.unknownLanguageLabel ?? MultilingualTextField.unknownLanguageLabel;
        MultilingualTextField.dialogTitle = settings.dialogTitle ?? MultilingualTextField.dialogTitle;
        MultilingualTextField.dialogSubTitle = settings.dialogSubTitle ?? MultilingualTextField.dialogSubTitle;
        MultilingualTextField.okButtonLabel = settings.okButtonLabel ?? MultilingualTextField.okButtonLabel;
        MultilingualTextField.cancelButtonLabel = settings.cancelButtonLabel ?? MultilingualTextField.cancelButtonLabel;
        return this;
    }

    public configurePasswordField(settings: {
        requiredLabel?: string;
    }): Components
    {
        PasswordField.requiredLabel = settings.requiredLabel ?? PasswordField.requiredLabel;
        return this;
    }

    public configureSelect(settings: {
        requiredLabel?: string;
    }): Components
    {
        Select.requiredLabel = settings.requiredLabel ?? Select.requiredLabel;
        return this;
    }

    public configureTextArea(settings: {
        requiredLabel?: string;
    }): Components
    {
        TextArea.requiredLabel = settings.requiredLabel ?? TextArea.requiredLabel;
        return this;
    }

    public configureTextField(settings: {
        requiredLabel?: string;
    }): Components
    {
        TextField.requiredLabel = settings.requiredLabel ?? TextField.requiredLabel;
        return this;
    }

    public configureUserPicker(settings: {

        label?: string;
        noUserSelectedLabel?: string;
    }): Components
    {
        UserPicker.label = settings.label ?? UserPicker.label;
        UserPicker.noUserSelectedLabel = settings.noUserSelectedLabel ?? UserPicker.noUserSelectedLabel;
        return this;
    }

    public configureTreeDataGrid(settings: {
        noDataLabel?: string;
    }): Components
    {
        TreeDataGrid.noDataLabel = settings.noDataLabel ?? TreeDataGrid.noDataLabel;
        return this;
    }

    public configureTickSymbol(settings: {
        enabledAriaLabel?: string;
        disabledAriaLabel?: string;
    }): Components
    {
        TickSymbol.enabledAriaLabel = settings.enabledAriaLabel ?? TickSymbol.enabledAriaLabel;
        TickSymbol.disabledAriaLabel = settings.disabledAriaLabel ?? TickSymbol.disabledAriaLabel;
        return this;
    }

    private readonly resizeListener = () => this.handleWindowResize();
    // --sd-system-dimension-breakpoint-expanded
    private getScreenSize(): ScreenSize { return (window.innerWidth < 768 ? 'compact' : 'expanded'); }
    public screenSize: ScreenSize = this.getScreenSize();

    private handleWindowResize(): void
    {
        if (this.getScreenSize() == this.screenSize)
        {
            return;
        }
        this.screenSize = this.getScreenSize();
        this.resizeHandlers.forEach(i => i(this.screenSize));
    }

    private resizeHandlers = new Map<string, (screenSize: ScreenSize) => void>();

    public addResizeHandler(handler: (screenSize: ScreenSize) => void, onAdded: (handlerId: string) => void): Components
    {
        const id = Guid.new().toString();
        this.resizeHandlers.set(id, handler);
        onAdded(id);
        return this;
    }

    public removeResizeHandler(id: string): Components
    {
        this.resizeHandlers.delete(id);
        return this;
    }

    private lastScrollTop = 0;
    public scrollDirection: '' | 'up' | 'down' = '';

    public handleLayoutContainerScroll(e: React.UIEvent<HTMLDivElement, UIEvent>): void
    {
        const target = e.target as HTMLElement;
        const scrollTop = target.scrollTop;
        this.scrollDirection = scrollTop > this.lastScrollTop ? 'down' : 'up';
        this.lastScrollTop = scrollTop;
        this.layoutContainerScrollHandlers.forEach(i => i(e));
    }

    private layoutContainerScrollHandlers = new Map<string, (e: React.UIEvent<HTMLDivElement, UIEvent>) => void>();

    public addLayoutContainerScrollHandler(handler: (e: React.UIEvent<HTMLDivElement, UIEvent>) => void, onAdded: (handlerId: string) => void): Components
    {
        const id = Guid.new().toString();
        this.layoutContainerScrollHandlers.set(id, handler);
        onAdded(id);
        return this;
    }

    public removeLayoutContainerScrollHandler(id: string): Components
    {
        this.layoutContainerScrollHandlers.delete(id);
        return this;
    }

    public dispose(): void
    {
        window.removeEventListener('resize', this.resizeListener);
    }
}

export type ScreenSize = 'compact' | 'expanded';
